import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { useAuth } from "@/lib/auth";
import { Hash, Pencil, Plus, RefreshCw, Settings2 } from "lucide-react";

const nzd = (n) =>
  new Intl.NumberFormat("en-NZ", { style: "currency", currency: "NZD", minimumFractionDigits: 0 }).format(n || 0);

const RES_ORDER = ["L", "P", "M", "S"];
const RES_COLORS = {
  L: "bg-blue-50 text-blue-700 border-blue-200",
  P: "bg-amber-50 text-amber-700 border-amber-200",
  M: "bg-emerald-50 text-emerald-700 border-emerald-200",
  S: "bg-purple-50 text-purple-700 border-purple-200",
  O: "bg-zinc-50 text-zinc-600 border-zinc-200",
};

export function CostCodeReport({ jobId, version = 0 }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" || user?.role === "super_admin";
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [estOpen, setEstOpen] = useState(false);
  const [manageOpen, setManageOpen] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      const { data } = await api.get(`/jobs/${jobId}/cost-code-report`);
      setReport(data);
    } catch (e) {
      if (e.response?.status !== 403) toast.error(e.response?.data?.detail || "Failed to load cost codes");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [jobId, version]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading && !report) {
    return <div className="text-[11px] font-mono uppercase tracking-widest text-zinc-500 mt-6">Loading cost codes…</div>;
  }
  if (!report) return null;

  const hasData = report.phases.length > 0 || report.uncoded.length > 0 || report.total_estimate > 0;

  return (
    <div className="mt-6 pt-5 border-t-2 border-black/10" data-testid={`cost-code-report-${jobId}`}>
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <div className="label-tag flex items-center gap-1.5">
          <Hash className="w-3 h-3" /> Cost code report — phase · activity · resource
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={load} data-testid="cost-code-refresh"
                  className="border-2 border-black h-8">
            <RefreshCw className="w-3.5 h-3.5" />
          </Button>
          {isAdmin && (
            <>
              <Button variant="outline" size="sm" onClick={() => setManageOpen(true)}
                      data-testid="cost-code-manage-btn" className="border-2 border-black h-8">
                <Settings2 className="w-3.5 h-3.5 mr-1" /> Codes
              </Button>
              <Button size="sm" onClick={() => setEstOpen(true)}
                      data-testid="cost-code-estimates-btn"
                      className="bg-black text-white hover:bg-zinc-800 h-8">
                <Pencil className="w-3.5 h-3.5 mr-1" /> Set estimates
              </Button>
            </>
          )}
        </div>
      </div>

      {!hasData ? (
        <div className="border-2 border-dashed border-zinc-300 p-5 text-center">
          <div className="text-sm text-zinc-600">
            No cost code estimates yet.
            {isAdmin ? " Set per-activity estimates split by Labour / Plant / Materials / Subbies to track spend against your tender build-up in real time." : ""}
          </div>
          {isAdmin && (
            <Button size="sm" onClick={() => setEstOpen(true)}
                    data-testid="cost-code-setup-btn"
                    className="bg-black text-white hover:bg-zinc-800 mt-3">
              <Plus className="w-3.5 h-3.5 mr-1" /> Set estimates
            </Button>
          )}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <Tot label="Estimated" value={nzd(report.total_estimate)} testid="cc-total-estimate" />
            <Tot label="Actual to date" value={nzd(report.total_actual)} testid="cc-total-actual" />
            <Tot label="Variance" value={nzd(report.total_variance)}
                 tint={report.total_variance < 0 ? "text-red-600" : "text-emerald-700"}
                 testid="cc-total-variance" />
            <Tot label="% spent" value={report.pct_spent != null ? `${report.pct_spent}%` : "—"}
                 tint={(report.pct_spent || 0) > 100 ? "text-red-600" : "text-zinc-900"}
                 testid="cc-total-pct" />
          </div>

          <div className="border border-zinc-200 overflow-x-auto">
            <table className="w-full text-sm min-w-[620px]">
              <thead>
                <tr className="bg-zinc-50 text-[10px] font-bold uppercase tracking-widest text-zinc-500 border-b border-zinc-200">
                  <th className="text-left px-3 py-2">Code</th>
                  <th className="text-left px-3 py-2">Activity</th>
                  <th className="text-left px-3 py-2">Res</th>
                  <th className="text-right px-3 py-2">Estimate</th>
                  <th className="text-right px-3 py-2">Actual</th>
                  <th className="text-right px-3 py-2">Variance</th>
                  <th className="text-right px-3 py-2">%</th>
                </tr>
              </thead>
              <tbody>
                {report.phases.map((ph) => (
                  <PhaseRows key={ph.phase_code} phase={ph} />
                ))}
                {report.uncoded.length > 0 && (
                  <>
                    <tr className="bg-zinc-100/70 border-t border-zinc-200" data-testid="cc-uncoded-header">
                      <td className="px-3 py-1.5 font-bold uppercase text-[11px] tracking-widest text-zinc-500" colSpan={4}>
                        Uncoded / auto actuals
                      </td>
                      <td className="px-3 py-1.5 text-right font-mono text-xs font-bold">{nzd(report.uncoded_total)}</td>
                      <td colSpan={2} />
                    </tr>
                    {report.uncoded.map((r) => (
                      <tr key={r.resource} className="border-t border-zinc-100" data-testid={`cc-uncoded-${r.resource}`}>
                        <td className="px-3 py-1.5 font-mono text-xs text-zinc-400">—</td>
                        <td className="px-3 py-1.5 text-zinc-500 italic">{r.label}</td>
                        <td className="px-3 py-1.5"><ResChip res={r.resource} /></td>
                        <td className="px-3 py-1.5 text-right font-mono text-xs text-zinc-400">—</td>
                        <td className="px-3 py-1.5 text-right font-mono text-xs">{nzd(r.actual)}</td>
                        <td className="px-3 py-1.5" colSpan={2} />
                      </tr>
                    ))}
                  </>
                )}
                <tr className="border-t-2 border-black font-bold bg-zinc-50" data-testid="cc-grand-total-row">
                  <td className="px-3 py-2" colSpan={3}>TOTAL</td>
                  <td className="px-3 py-2 text-right font-mono">{nzd(report.total_estimate)}</td>
                  <td className="px-3 py-2 text-right font-mono">{nzd(report.total_actual)}</td>
                  <td className={`px-3 py-2 text-right font-mono ${report.total_variance < 0 ? "text-red-600" : "text-emerald-700"}`}>
                    {nzd(report.total_variance)}
                  </td>
                  <td className="px-3 py-2 text-right font-mono">
                    {report.pct_spent != null ? `${report.pct_spent}%` : "—"}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div className="flex flex-wrap items-center gap-3 mt-2">
            {RES_ORDER.map((r) => (
              <span key={r} className="text-[10px] font-mono text-zinc-500">
                <span className={`inline-block border px-1 mr-1 font-bold ${RES_COLORS[r]}`}>{r}</span>
                {report.resources?.[r]}
              </span>
            ))}
          </div>
          {report.estimates_updated_at && (
            <div className="text-[10px] font-mono text-zinc-500 mt-2">
              Estimates last updated {new Date(report.estimates_updated_at).toLocaleDateString("en-NZ")}
              {report.estimates_updated_by ? ` by ${report.estimates_updated_by}` : ""}
            </div>
          )}
        </>
      )}

      {estOpen && (
        <EstimatesDialog
          jobId={jobId} report={report}
          onClose={() => setEstOpen(false)}
          onSaved={(r) => { setReport(r); setEstOpen(false); toast.success("Estimates saved"); }}
        />
      )}
      {manageOpen && (
        <ManageCodesDialog onClose={() => { setManageOpen(false); load(); }} />
      )}
    </div>
  );
}

function ResChip({ res }) {
  return (
    <span className={`inline-block border px-1.5 py-0.5 text-[10px] font-bold font-mono ${RES_COLORS[res] || RES_COLORS.O}`}>
      {res}
    </span>
  );
}

function Tot({ label, value, tint, testid }) {
  return (
    <div className="bg-white border-2 border-zinc-100 p-3" data-testid={testid}>
      <div className="text-[9px] font-bold uppercase tracking-widest text-zinc-500">{label}</div>
      <div className={`font-display font-bold text-lg mt-0.5 ${tint || "text-zinc-900"}`}>{value}</div>
    </div>
  );
}

function PhaseRows({ phase }) {
  return (
    <>
      <tr className="bg-zinc-100/70 border-t border-zinc-200" data-testid={`cc-phase-${phase.phase_code}`}>
        <td className="px-3 py-1.5 font-mono text-[11px] font-bold">{phase.phase_code}</td>
        <td className="px-3 py-1.5 font-bold uppercase text-[11px] tracking-widest" colSpan={2}>{phase.phase_name}</td>
        <td className="px-3 py-1.5 text-right font-mono text-xs font-bold">{nzd(phase.estimate)}</td>
        <td className="px-3 py-1.5 text-right font-mono text-xs font-bold">{nzd(phase.actual)}</td>
        <td className={`px-3 py-1.5 text-right font-mono text-xs font-bold ${phase.variance < 0 ? "text-red-600" : "text-emerald-700"}`}>
          {nzd(phase.variance)}
        </td>
        <td className="px-3 py-1.5 text-right font-mono text-xs font-bold">
          {phase.pct_spent != null ? `${phase.pct_spent}%` : "—"}
        </td>
      </tr>
      {phase.rows.map((r) => (
        <tr key={r.full_code} className="border-t border-zinc-100 hover:bg-zinc-50"
            data-testid={`cc-row-${r.full_code}`}>
          <td className="px-3 py-1.5 font-mono text-xs text-zinc-600 font-bold">{r.full_code}</td>
          <td className="px-3 py-1.5">{r.name}</td>
          <td className="px-3 py-1.5"><ResChip res={r.resource} /></td>
          <td className="px-3 py-1.5 text-right font-mono text-xs">{r.estimate ? nzd(r.estimate) : "—"}</td>
          <td className="px-3 py-1.5 text-right font-mono text-xs">{nzd(r.actual)}</td>
          <td className={`px-3 py-1.5 text-right font-mono text-xs ${r.variance != null && r.variance < 0 ? "text-red-600 font-bold" : "text-zinc-700"}`}>
            {r.variance != null ? nzd(r.variance) : "—"}
          </td>
          <td className={`px-3 py-1.5 text-right font-mono text-xs ${(r.pct_spent || 0) > 100 ? "text-red-600 font-bold" : ""}`}>
            {r.pct_spent != null ? `${r.pct_spent}%` : "—"}
          </td>
        </tr>
      ))}
    </>
  );
}

function EstimatesDialog({ jobId, report, onClose, onSaved }) {
  const [vals, setVals] = useState(() => {
    const m = {};
    for (const cc of report.cost_codes) {
      const byRes = report.estimates?.[cc.id] || {};
      for (const res of RES_ORDER) {
        m[`${cc.id}:${res}`] = byRes[res] ? String(byRes[res]) : "";
      }
    }
    return m;
  });
  const [saving, setSaving] = useState(false);

  const byPhase = {};
  for (const cc of report.cost_codes) {
    (byPhase[cc.phase_code] = byPhase[cc.phase_code] || []).push(cc);
  }
  const phaseName = (code) => report.phase_list?.find((p) => p.code === code)?.name || code;
  const sum = Object.values(vals).reduce((s, v) => s + (parseFloat(v) || 0), 0);

  const save = async () => {
    setSaving(true);
    try {
      const items = Object.entries(vals)
        .map(([k, v]) => {
          const [cost_code_id, resource] = k.split(":");
          return { cost_code_id, resource, amount: parseFloat(v) || 0 };
        })
        .filter((r) => r.amount > 0);
      const { data } = await api.put(`/jobs/${jobId}/cost-code-estimates`, { items });
      onSaved(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save estimates");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="cc-estimates-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Cost code estimates</DialogTitle>
        </DialogHeader>
        <div className="text-[11px] font-mono text-zinc-500 -mt-2">
          Split each activity by resource: L Labour · P Plant · M Materials · S Subbies
        </div>
        <div className="space-y-5">
          {Object.entries(byPhase).sort().map(([pc, codes]) => (
            <div key={pc}>
              <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500 mb-2 border-b border-zinc-200 pb-1">
                {pc} · {phaseName(pc)}
              </div>
              <div className="space-y-2">
                {codes.map((cc) => (
                  <div key={cc.id} className="flex flex-wrap items-center gap-2">
                    <span className="font-mono text-xs font-bold w-14 shrink-0">{cc.code}</span>
                    <span className="text-sm flex-1 min-w-[120px] truncate">{cc.name}</span>
                    {RES_ORDER.map((res) => (
                      <div key={res} className="flex items-center gap-1">
                        <span className={`border px-1 text-[10px] font-bold font-mono ${RES_COLORS[res]}`}>{res}</span>
                        <Input
                          type="number" step="0.01"
                          value={vals[`${cc.id}:${res}`] || ""}
                          onChange={(e) => setVals({ ...vals, [`${cc.id}:${res}`]: e.target.value })}
                          data-testid={`cc-est-input-${cc.code}-${res}`}
                          placeholder="0"
                          className="border-2 border-black w-[74px] h-8 px-1.5 text-xs"
                        />
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          ))}
          <div className="text-sm font-bold border-t-2 border-black pt-2 flex justify-between">
            <span>Total estimate</span>
            <span className="font-mono" data-testid="cc-est-sum">{nzd(sum)}</span>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="border-2 border-black">Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="cc-est-save-btn"
                  className="bg-black text-white hover:bg-zinc-800">
            {saving ? "Saving…" : "Save estimates"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ManageCodesDialog({ onClose }) {
  const [codes, setCodes] = useState([]);
  const [phases, setPhases] = useState([]);
  const [form, setForm] = useState({ code: "", name: "", phase_code: "" });
  const [phaseForm, setPhaseForm] = useState({ code: "", name: "" });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const { data } = await api.get("/settings/cost-codes?include_inactive=true");
    setCodes(data.cost_codes || []);
    setPhases(data.phases || []);
    if (!form.phase_code && data.phases?.length) {
      setForm((f) => ({ ...f, phase_code: data.phases[0].code }));
    }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const addCode = async () => {
    if (!form.code.trim() || !form.name.trim()) return toast.error("Code and name required");
    setBusy(true);
    try {
      await api.post("/settings/cost-codes", form);
      toast.success("Activity code added");
      setForm({ ...form, code: "", name: "" });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  const addPhase = async () => {
    if (!phaseForm.code.trim() || !phaseForm.name.trim()) return toast.error("Code and name required");
    try {
      await api.post("/settings/cost-phases", phaseForm);
      toast.success("Phase added");
      setPhaseForm({ code: "", name: "" });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const toggle = async (cc) => {
    try {
      await api.patch(`/settings/cost-codes/${cc.id}`, { active: !cc.active });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="cc-manage-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Cost code library</DialogTitle>
        </DialogHeader>

        <div className="flex flex-wrap items-end gap-2 border-2 border-zinc-100 p-3">
          <div>
            <Label className="text-[10px] uppercase tracking-widest">Activity code</Label>
            <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })}
                   placeholder="03-40" data-testid="cc-new-code-input"
                   className="border-2 border-black w-24 h-9 mt-1" />
          </div>
          <div className="flex-1 min-w-[140px]">
            <Label className="text-[10px] uppercase tracking-widest">Name</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                   placeholder="e.g. Culvert Installation" data-testid="cc-new-name-input"
                   className="border-2 border-black h-9 mt-1" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest">Phase</Label>
            <Select value={form.phase_code} onValueChange={(v) => setForm({ ...form, phase_code: v })}>
              <SelectTrigger className="border-2 border-black w-52 h-9 mt-1" data-testid="cc-new-phase-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {phases.filter((p) => p.active !== false).map((p) => (
                  <SelectItem key={p.code} value={p.code}>{p.code} · {p.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button onClick={addCode} disabled={busy} data-testid="cc-add-code-btn"
                  className="bg-black text-white hover:bg-zinc-800 h-9">
            <Plus className="w-3.5 h-3.5 mr-1" /> Add
          </Button>
        </div>

        <div className="flex flex-wrap items-end gap-2 border-2 border-dashed border-zinc-200 p-3">
          <div>
            <Label className="text-[10px] uppercase tracking-widest">New phase code</Label>
            <Input value={phaseForm.code} onChange={(e) => setPhaseForm({ ...phaseForm, code: e.target.value })}
                   placeholder="06-00" data-testid="cc-new-phase-code-input"
                   className="border-2 border-black w-24 h-9 mt-1" />
          </div>
          <div className="flex-1 min-w-[140px]">
            <Label className="text-[10px] uppercase tracking-widest">Phase name</Label>
            <Input value={phaseForm.name} onChange={(e) => setPhaseForm({ ...phaseForm, name: e.target.value })}
                   placeholder="e.g. Landscaping & Reinstatement" data-testid="cc-new-phase-name-input"
                   className="border-2 border-black h-9 mt-1" />
          </div>
          <Button onClick={addPhase} variant="outline" data-testid="cc-add-phase-btn"
                  className="border-2 border-black h-9">
            <Plus className="w-3.5 h-3.5 mr-1" /> Add phase
          </Button>
        </div>

        <div className="space-y-3 mt-2">
          {phases.map((ph) => {
            const phCodes = codes.filter((c) => c.phase_code === ph.code);
            if (phCodes.length === 0) return null;
            return (
              <div key={ph.code}>
                <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500 mb-1">
                  {ph.code} · {ph.name}
                </div>
                <div className="divide-y divide-zinc-100 border border-zinc-200">
                  {phCodes.map((cc) => (
                    <div key={cc.id} className={`flex items-center gap-3 p-2.5 ${cc.active ? "" : "opacity-40"}`}
                         data-testid={`cc-lib-row-${cc.code}`}>
                      <span className="font-mono text-xs font-bold w-16 shrink-0">{cc.code}</span>
                      <span className="text-sm flex-1 truncate">{cc.name}</span>
                      <button onClick={() => toggle(cc)}
                              data-testid={`cc-toggle-${cc.code}`}
                              className="text-[10px] font-bold uppercase tracking-widest text-zinc-600 hover:text-black border border-zinc-300 px-2 py-1">
                        {cc.active ? "Disable" : "Enable"}
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="border-2 border-black">Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
